import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  Lock,
  User,
  Eye,
  EyeOff,
  Shield,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-12 h-12" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

export default function AdminLogin() {
  const navigate = useNavigate();
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      setLoading(true);
      const data = await api.verifyAdmin(username, password);
      
      if (data.success) {
        localStorage.setItem('panchi_admin_token', data.token || 'admin_session');
        toast.success('Welcome, Admin! 👋');
        navigate('/admin');
      } else {
        toast.error(data.message || 'Invalid credentials');
      }
    } catch (error) {
      toast.error('Authentication failed');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-72 h-72 bg-teal-200/40 blur-3xl" style={{ borderRadius: '50%' }} />
        <div className="absolute bottom-20 -right-20 w-96 h-96 bg-cyan-200/40 blur-3xl" style={{ borderRadius: '50%' }} />
      </div>
      
      {/* Back Button */}
      <button 
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors z-20"
        data-testid="back-home"
      >
        <ArrowLeft size={20} />
        <span>Home</span>
      </button>
      
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-slate-700 to-slate-900 mb-6 shadow-lg"
            style={{ borderRadius: '1.5rem' }}
          >
            <Shield size={40} className="text-white" />
          </motion.div>
          <h1 className="text-3xl font-black text-gray-900 mb-2">Admin Portal</h1>
          <p className="text-gray-500">Panchi Dashboard Access</p>
        </div>
        
        {/* Login Card */}
        <div className="p-6 md:p-8 bg-white/80 backdrop-blur-xl border border-white/50 shadow-xl" style={{ borderRadius: '1.5rem' }}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-slate-600 focus:bg-white outline-none transition-all"
                style={{ borderRadius: '9999px' }}
                data-testid="username-input"
              />
            </div>
            
            <div className="relative">
              <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full pl-12 pr-12 py-3.5 bg-gray-50 border-2 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-slate-600 focus:bg-white outline-none transition-all"
                style={{ borderRadius: '9999px' }}
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-slate-700 to-slate-900 text-white font-bold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all disabled:opacity-50"
              style={{ borderRadius: '9999px' }}
              data-testid="login-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={18} className="animate-spin" />
                  Authenticating...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Shield size={18} />
                  Access Dashboard
                </span>
              )}
            </button>
          </form>
        </div>
        
        {/* Footer Note */}
        <p className="text-center text-gray-400 text-sm mt-6">
          Authorized personnel only. All access is logged.
        </p>
      </motion.div>
    </div>
  );
}
